/* eslint-disable no-unused-expressions */
import { useCookies } from 'react-cookie';
import { useQuery } from 'react-query';

import { useContent } from '@hooks/useContent';
import { useHttpService } from '@hooks/useHttpService';
import { useLocale } from '@hooks/useLocale';
import { useUser } from '@hooks/user/useUser';

import { useCartId, CART_STORAGE_KEY } from './useCartId';
import { isCartItemOnStock } from '../../helpers/isCartItemOnStock';

export const CART_QUERY_ID = 'cartV2';

export const useCart = ({ onEmptyCart, onSuccess } = {}) => {
  const [, setCookie, removeCookie] = useCookies([CART_STORAGE_KEY]);

  const httpService = useHttpService();
  const { data: user } = useUser();
  const cartId = useCartId();
  const { locale, country } = useLocale();
  const { data: plansContent } = useContent({ locale, name: `plans-${country}` });

  return useQuery(
    CART_QUERY_ID,
    async () => {
      if (!cartId) {
        const { data } = await httpService.post('/cart', { userId: user?.id });

        return data;
      }

      const params = {
        isProcessedToPayment: false,
        country,
      };

      const getCartUrl = user ? '/cart/user' : '/cart';
      const { data } = await httpService.get(`${getCartUrl}/${cartId}`, {
        params,
      });

      return data;
    },
    {
      enabled: !!plansContent,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: 0,
      onError: error => {
        if (error.status === 404) {
          removeCookie([CART_STORAGE_KEY], { path: '/', sameSite: 'strict', secure: true });
        }
      },
      onSuccess: data => {
        if (data?.items?.length === 0) {
          onEmptyCart?.();
        }

        if (data?.result === 'CART_CREATED') {
          setCookie(CART_STORAGE_KEY, data.cart.id, { path: '/', sameSite: 'strict', secure: true });
        }

        if (onSuccess) {
          onSuccess(data);
        }
      },
      select: cart => {
        if (!cart?.items) {
          return cart;
        }

        const itemsWithStockInformationAndContent = cart.items.map(item => ({
          ...item,
          content:
            plansContent?.[item?.plan?.bmId?.toLowerCase()] || plansContent?.[item?.plan?.product?.vib?.toLowerCase()],
          isOnStock: isCartItemOnStock(item),
        }));

        return {
          ...cart,
          items: itemsWithStockInformationAndContent,
          hasOutOfStockItems: itemsWithStockInformationAndContent.some(item => !item.isOnStock),
          numberOfAvailableItems: itemsWithStockInformationAndContent.filter(item => item.isOnStock).length,
          numberOfOutOfStockItems: itemsWithStockInformationAndContent.filter(item => !item.isOnStock).length,
        };
      },
    }
  );
};
